import React, { useEffect, useState, useMemo } from "react";
import Meta from "./../components/Meta";
import { useRouter } from "../util/router";
import { useAuth } from "../util/auth";
import { useEventName, useEventTdId, useUsersName } from "../util/db";
import CtaSection from "../components/CtaSection";
import HrCustom from "../components/HrCustom";
import { Container, Spinner, Alert, Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  useEventEntriesByEventUser,
  useGroupScorecardsByEventUser,
  useForumPostsNotHidden,
} from "../util/db";
import "../components/SectionHeader.scss";
import "./event.scss";
import EventPageTabs from "../components/EventPageTabs";
import EventPageHeader from "../components/EventPageHeader";

function EventPage(props) {
  const router = useRouter();
  const auth = useAuth();
  const eventId = router.query.event;
  const initialTab = router.query.tab;
  const uid = auth.user?.uid || null;

  // // Debug logging
  // console.log("Event page - router.query:", router.query);
  // console.log("Event page - eventId:", eventId);

  // Event details state
  const [eventDetails, setEventDetails] = useState({
    name: "",
    date: null,
    course: "",
    courseId: null,
    gameName: "",
    gameId: null,
    scoringOrderGolf: null,
    throwScoringModeId: 1,
    isComplete: false,
    roundId: null,
  });

  // User state
  const [userState, setUserState] = useState({
    isLoggedIn: false,
    canCheckIn: true,
    isIncompleteCards: false,
    isCheckedInAlready: false,
    tdId: "",
    tdName: "",
  });

  // Scroll state for header enhancement
  const [isScrolled, setIsScrolled] = useState(false);
  // Event details modal state
  const [showEventDetails, setShowEventDetails] = useState(false);
  const { data: posts } = useForumPostsNotHidden(eventId);
  const { data: entries } = useEventEntriesByEventUser(eventId, uid, {
    enabled: !!uid,
    refetchOnWindowFocus: true, // Ensure fresh data when returning to the page
    staleTime: 0, // Always consider data stale to encourage refetching
  });
  const { data: scorecards, status: scorecardsStatus } =
    useGroupScorecardsByEventUser(eventId, uid, {
      enabled: !!uid,
      refetchOnWindowFocus: true, // Ensure fresh data when returning to the page
      staleTime: 0, // Always consider data stale to encourage refetching
    });
  const { data: items, status: itemsStatus } = useEventName(eventId);
  const { data: itemsTd, status: itemsStatusTd } = useEventTdId(eventId);
  const { data: tdName, status: tdNameStatus } = useUsersName(userState.tdId, {
    enabled: !!userState.tdId, // Only run query when tdId has a value
  });

  useEffect(() => {
    //if the scorecard has loaded, and player is on a scorecard
    if (scorecardsStatus !== "loading" && scorecards) {
      let hasIncompleteCards = false;
      let canCheckInValue = true;

      //loop through scorecards
      scorecards.forEach((scorecard) => {
        //if any scorecard is not complete, set is incomplete cards to true (to trigger display of card in progress) and can check in to false
        if (!scorecard.group_scorecard.is_complete) {
          hasIncompleteCards = true;
          canCheckInValue = false;
        }
      });

      setUserState((prev) => ({
        ...prev,
        isIncompleteCards: hasIncompleteCards,
        canCheckIn: canCheckInValue,
      }));
    }

    // Handle check-in status with careful race condition prevention
    if (entries && scorecards && scorecardsStatus !== "loading") {
      // If there are entries but no scorecards, player is checked in but games haven't started
      if (entries.length > 0 && scorecards.length === 0) {
        setUserState((prev) => ({
          ...prev,
          isCheckedInAlready: true,
          canCheckIn: false,
        }));
      }
      // If there are more entries than scorecards, some entries don't have scorecards yet
      else if (entries.length > scorecards.length) {
        setUserState((prev) => ({
          ...prev,
          isCheckedInAlready: true,
          canCheckIn: false,
        }));
      }
      // If entries and scorecards are equal or player has no entries, clear the checked in flag
      else {
        setUserState((prev) => ({
          ...prev,
          isCheckedInAlready: false,
        }));
      }
    }
  }, [scorecards, entries, scorecardsStatus]);

  useEffect(() => {
    setUserState((prev) => ({
      ...prev,
      isLoggedIn: !!uid,
    }));
  }, [uid]);

  useEffect(() => {
    if (itemsStatus !== "loading" && items?.length > 0) {
      const item = items[0];

      const eventDate = new Date(item.date);
      const utcDate = new Date(
        eventDate.getUTCFullYear(),
        eventDate.getUTCMonth(),
        eventDate.getUTCDate()
      );

      setEventDetails({
        name: item.name,
        course: item.courses.course_name,
        courseId: item.course_id,
        scoringOrderGolf: item.scoring_order_golf,
        gameName: item.games.name,
        isComplete: item.is_event_complete,
        gameId: item.game_id,
        throwScoringModeId: item.throw_scoring_mode_id,
        roundId: item.round_id,
        date: utcDate.toLocaleDateString(),
      });
    }
  }, [items, itemsStatus]);

  useEffect(() => {
    if (itemsStatusTd !== "loading" && itemsTd?.length > 0) {
      setUserState((prev) => ({
        ...prev,
        tdId: itemsTd[0].td_id,
      }));
    }
  }, [itemsTd, itemsStatusTd]);

  // Effect to handle TD name
  useEffect(() => {
    if (tdNameStatus !== "loading" && tdName?.length > 0) {
      setUserState((prev) => ({
        ...prev,
        tdName: tdName[0].name,
      }));
    }
  }, [tdName, tdNameStatus]);

  const buttonPath = useMemo(
    () => `/auth/signin?next=/event?event=${eventId}`,
    [eventId]
  );

  // Dynamic page title
  const pageTitle = useMemo(() => {
    if (!eventDetails.name) return "Event Details";
    if (eventDetails.course) {
      return `${eventDetails.name} - ${eventDetails.course}`;
    }
    return eventDetails.name;
  }, [eventDetails.name, eventDetails.course]);

  // Update document title dynamically
  useEffect(() => {
    if (eventDetails.name) {
      document.title = pageTitle;
    }
    // }, [pageTitle]);
  }, [pageTitle, eventDetails.name]);

  // Handle scroll for header enhancement
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 20;
      setIsScrolled(scrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Show loading state while essential event data is loading
  const isLoading = itemsStatus === "loading" || itemsStatusTd === "loading";

  // Check for error states
  const hasError = itemsStatus === "error" || itemsStatusTd === "error";
  const eventNotFound =
    itemsStatus === "success" && (!items || items.length === 0);

  if (isLoading) {
    return (
      <div className="event-page">
        <Meta title="Loading Event..." />
        <Container className="loading-container d-flex flex-column align-items-center justify-content-center py-5">
          <Spinner
            animation="border"
            role="status"
            className="mb-4"
            size="lg"
          />
          <h5 className="text-muted mb-0">Loading event details...</h5>
        </Container>
      </div>
    );
  }

  if (hasError || eventNotFound) {
    return (
      <div className="event-page">
        <Meta title="Event Not Found" />
        <Container className="error-container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-xl-6">
              <Alert variant="danger" className="text-center shadow-sm">
                <Alert.Heading className="mb-3">
                  {eventNotFound ? "Event Not Found" : "Unable to Load Event"}
                </Alert.Heading>
                <p className="mb-4 text-muted">
                  {eventNotFound
                    ? "The event you're looking for doesn't exist or may have been removed."
                    : "We're having trouble loading the event details. Please try again."}
                </p>
                <div className="d-flex gap-3 justify-content-center flex-wrap">
                  <Button
                    variant="outline-primary"
                    onClick={() => window.location.reload()}
                    size="lg"
                  >
                    Try Again
                  </Button>
                  <Button variant="primary" href="/" size="lg">
                    Go to Home
                  </Button>
                </div>
              </Alert>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="event-page">
      <Meta title={pageTitle} />
      {!userState.isLoggedIn && (
        <>
          <CtaSection
            bg="primary"
            textColor="light"
            size="sm"
            bgImage=""
            bgImageOpacity={1}
            title="Ready to play?"
            subtitle=""
            buttonText="Log In"
            buttonPath={buttonPath}
          />
          <HrCustom width={"100%"} />
        </>
      )}

      <div
        className={`event-header-section sticky-top bg-white shadow-sm ${
          isScrolled ? "scrolled" : ""
        }`}
      >
        <EventPageHeader
          eventName={eventDetails.name}
          eventDate={eventDetails.date}
          onClick={() => setShowEventDetails(true)}
        />
        {((userState.isLoggedIn && userState.tdId === uid) ||
          eventDetails.roundId) && (
          <Container className="pb-3">
            <div className="text-center">
              {userState.isLoggedIn && userState.tdId === uid && (
                <div className="mb-2">
                  <Link
                    to={`/event_manage?event=${eventId}`}
                    style={{
                      color: "white",
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      padding: "6px 16px",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      borderRadius: "20px",
                      transition: "all 0.2s ease",
                      display: "inline-block",
                      backdropFilter: "blur(10px)",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor =
                        "rgba(255, 255, 255, 0.25)";
                      e.target.style.borderColor = "rgba(255, 255, 255, 0.5)";
                      e.target.style.transform = "translateY(-1px)";
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(0, 0, 0, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor =
                        "rgba(255, 255, 255, 0.15)";
                      e.target.style.borderColor = "rgba(255, 255, 255, 0.3)";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    ⚙️ Manage Event
                  </Link>
                </div>
              )}
              {eventDetails.roundId && (
                <div>
                  <a
                    href={`https://www.kpleagues.com/lc.php?round=${eventDetails.roundId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "white",
                      backgroundColor: "rgba(255, 255, 255, 0.15)",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      padding: "6px 16px",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      borderRadius: "20px",
                      transition: "all 0.2s ease",
                      display: "inline-block",
                      backdropFilter: "blur(10px)",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor =
                        "rgba(255, 255, 255, 0.25)";
                      e.target.style.borderColor = "rgba(255, 255, 255, 0.5)";
                      e.target.style.transform = "translateY(-1px)";
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(0, 0, 0, 0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor =
                        "rgba(255, 255, 255, 0.15)";
                      e.target.style.borderColor = "rgba(255, 255, 255, 0.3)";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    🏆 League Central
                  </a>
                </div>
              )}
            </div>
          </Container>
        )}
      </div>

      <div className="event-tabs-section">
        <EventPageTabs
          isIncompleteCards={userState.isIncompleteCards}
          isCheckedInAlready={userState.isCheckedInAlready}
          canCheckIn={userState.canCheckIn}
          eventId={eventId}
          uid={uid}
          isLoggedIn={userState.isLoggedIn}
          isEventComplete={eventDetails.isComplete}
          auth={auth}
          gameId={eventDetails.gameId}
          posts={posts}
          eventName={eventDetails.name}
          eventCourse={eventDetails.course}
          eventDate={eventDetails.date}
          eventGameName={eventDetails.gameName}
          eventCourseId={eventDetails.courseId}
          scoringOrderGolf={eventDetails.scoringOrderGolf}
          throwScoringModeId={eventDetails.throwScoringModeId}
          tdName={userState.tdName}
          tdId={userState.tdId}
          initialTab={initialTab}
        />
      </div>

      {/* Event Details Modal */}
      <Modal
        show={showEventDetails}
        onHide={() => setShowEventDetails(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: "#2D083F",
            color: "white",
            borderBottom: "none",
          }}
        >
          <Modal.Title style={{ color: "white" }}>
            Event Information
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#FFFFFF", paddingTop: "2rem" }}>
          <div className="mb-3 pb-3 border-bottom">
            <strong className="text-muted d-block mb-1">Event</strong>
            <span className="h6 mb-0" style={{ color: "#495057" }}>
              {eventDetails.name}
            </span>
          </div>

          <div className="mb-3 pb-3 border-bottom">
            <strong className="text-muted d-block mb-1">Course</strong>
            <Link
              to={`/venue?id=${eventDetails.courseId}`}
              className="h6 mb-0 text-decoration-none"
              style={{ color: "#2D083F" }}
              onClick={() => setShowEventDetails(false)}
            >
              {eventDetails.course}
            </Link>
          </div>

          <div className="mb-3 pb-3 border-bottom">
            <strong className="text-muted d-block mb-1">Game</strong>
            <span className="h6 mb-0" style={{ color: "#495057" }}>
              {eventDetails.gameName}
            </span>
          </div>

          <div
            className={`mb-3 ${userState.tdName ? "pb-3 border-bottom" : ""}`}
          >
            <strong className="text-muted d-block mb-1">Date</strong>
            <span className="h6 mb-0" style={{ color: "#495057" }}>
              {eventDetails.date}
            </span>
          </div>

          {userState.tdName && (
            <div className="mb-0">
              <strong className="text-muted d-block mb-1">
                Event Director
              </strong>
              <Link
                to={`/player?id=${userState.tdId}`}
                className="h6 mb-0 text-decoration-none"
                style={{ color: "#2D083F" }}
                onClick={() => setShowEventDetails(false)}
              >
                {userState.tdName}
              </Link>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: "#2D083F", borderTop: "none" }}>
          <Button
            variant="light"
            onClick={() => setShowEventDetails(false)}
            style={{
              backgroundColor: "white",
              color: "#2D083F",
              border: "1px solid white",
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default EventPage;
